import type {
  TypeAction,
  PaletteColor,
  ColorSystemOptions,
  PaletteColorChannel,
} from '@mui/material/styles';
import type { SchemesRecord } from '../types';

import { varAlpha, createPaletteChannel } from 'minimal-shared/utils';

import { opacity } from './opacity';
import { themeConfig } from '../theme-config';

// ----------------------------------------------------------------------

/**
 * TypeScript extension for MUI theme augmentation.
 * @to {@link file://./../extend-theme-types.d.ts}
 */

// Keys for core palette colors
export type PaletteColorKey = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';
export type CommonColorsKeys = 'black' | 'white';

// Palette color without channels
export type PaletteColorNoChannels = Omit<PaletteColor, 'lighterChannel' | 'darkerChannel'>;

// Palette color with channels
export type PaletteColorWithChannels = PaletteColor & PaletteColorChannel;

// Extended palette color shades
export type PaletteColorExtend = {
  lighter: string;
  darker: string;
  lighterChannel: string;
  darkerChannel: string;
};

// Extended common colors
export type CommonColorsExtend = {
  whiteChannel: string;
  blackChannel: string;
};

// Extended text colors
export type TypeTextExtend = {
  disabledChannel: string;
};

// Extended background colors
export type TypeBackgroundExtend = {
  neutral: string;
  neutralChannel: string;
};

// Extended grey colors
export type GreyExtend = {
  '50Channel': string;
  '100Channel': string;
  '200Channel': string;
  '300Channel': string;
  '400Channel': string;
  '500Channel': string;
  '600Channel': string;
  '700Channel': string;
  '800Channel': string;
  '900Channel': string;
};

// Extended palette
export type PaletteExtend = {
  shared: {
    inputOutlined: string;
    inputUnderline: string;
    paperOutlined: string;
    buttonOutlined: string;
  };
};

/**
 * ➤
 * ➤ ➤ Core palette (primary, secondary, info, success, warning, error, common, grey)
 * ➤
 */
export const primary = createPaletteChannel(themeConfig.palette.primary);
export const secondary = createPaletteChannel(themeConfig.palette.secondary);
export const info = createPaletteChannel(themeConfig.palette.info);
export const success = createPaletteChannel(themeConfig.palette.success);
export const warning = createPaletteChannel(themeConfig.palette.warning);
export const error = createPaletteChannel(themeConfig.palette.error);
export const common = createPaletteChannel(themeConfig.palette.common);
export const grey = createPaletteChannel(themeConfig.palette.grey);

/**
 * ➤
 * ➤ ➤ Text, background, action
 * ➤
 */
// ── True dark surface tokens ───────────────────────────────────────────────
// These sit outside the grey scale so they're always available
// regardless of which grey shade maps to what in a given mode.
const dark = {
  page:      '#0e0e0e',   // page / body background
  surface:   '#111111',   // cards, panels, paper
  elevated:  '#171717',   // elevated surfaces, dropdowns, modals
  border:    '#1e1e1e',   // dividers and borders
  highlight: '#222222',   // hover states, table row hover
} as const;

// ── Text ──────────────────────────────────────────────────────────────────
export const text = {
  light: createPaletteChannel({
    primary:   grey[800],   // #252320 — rich warm dark
    secondary: grey[500],   // #7a746c — mid stone
    disabled:  grey[400],   // #a8a39a — muted
  }),
  dark: createPaletteChannel({
    primary:   '#FFFFFF',             // pure white on true dark
    secondary: grey[400],             // #a8a39a — warm mid tone
    disabled:  grey[600],             // #57524c — muted on dark
  }),
};

// ── Background ────────────────────────────────────────────────────────────
export const background = {
  light: createPaletteChannel({
    paper:   '#FFFFFF',       // card / surface
    default: grey[100],       // #f4f3ee — warm stone page bg
    neutral: grey[200],       // #e8e6df — subtle secondary surface
  }),
  dark: createPaletteChannel({
    paper:   dark.surface,    // #111111 — card / panel surface
    default: dark.page,       // #0e0e0e — page background
    neutral: dark.elevated,   // #171717 — elevated surface
  }),
};

// ── Action ────────────────────────────────────────────────────────────────
export const action = (mode: 'light' | 'dark'): Partial<TypeAction> => ({
  active:            mode === 'light' ? grey[600]  : grey[400],
  hover:             mode === 'light'
                       ? varAlpha(grey['500Channel'], 0.08)
                       : 'rgba(255,255,255,0.06)',
  selected:          mode === 'light'
                       ? varAlpha(grey['500Channel'], 0.16)
                       : 'rgba(255,255,255,0.10)',
  focus:             mode === 'light'
                       ? varAlpha(grey['500Channel'], 0.24)
                       : 'rgba(255,255,255,0.14)',
  disabled:          mode === 'light'
                       ? varAlpha(grey['500Channel'], 0.8)
                       : 'rgba(255,255,255,0.3)',
  disabledBackground: mode === 'light'
                       ? varAlpha(grey['500Channel'], 0.24)
                       : 'rgba(255,255,255,0.08)',
  hoverOpacity:      0.08,
  selectedOpacity:   0.08,
  focusOpacity:      0.12,
  activatedOpacity:  0.12,
  disabledOpacity:   0.48,
});

// ── Extended palette ──────────────────────────────────────────────────────
export const extendPalette: PaletteExtend = {
  shared: {
    inputUnderline:  varAlpha(grey['500Channel'], opacity.inputUnderline),
    inputOutlined:   varAlpha(grey['500Channel'], 0.2),
    paperOutlined:   varAlpha(grey['500Channel'], 0.16),
    buttonOutlined:  varAlpha(grey['500Channel'], 0.32),
  },
};

// ── Base palette (shared between light and dark) ───────────────────────────
const basePalette: ColorSystemOptions['palette'] = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  common,
  grey,
  divider: varAlpha(grey['500Channel'], 0.2),
  TableCell: { border: varAlpha(grey['500Channel'], 0.2) },
  ...extendPalette,
};

/* **********************************************************************
 * 📦 Final palette export
 *
 * Light: warm stone backgrounds, dark stone text
 * Dark:  true black backgrounds (#0e0e0e / #111),
 *        white text, cobalt accent unchanged
 * **********************************************************************/
export const palette: SchemesRecord<ColorSystemOptions['palette']> = {
  light: {
    ...basePalette,
    text:       text.light,
    background: background.light,
    action:     action('light'),
    divider:    varAlpha(grey['500Channel'], 0.16),
  },
  dark: {
    ...basePalette,
    text:       text.dark,
    background: background.dark,
    action:     action('dark'),
    // Divider is lighter on true dark so it's visible without being harsh
    divider:    'rgba(255,255,255,0.08)',
    // Override TableCell border for dark
    TableCell:  { border: 'rgba(255,255,255,0.08)' },
    // Override shared border utilities for dark
    shared: {
      inputUnderline:  'rgba(255,255,255,0.12)',
      inputOutlined:   'rgba(255,255,255,0.14)',
      paperOutlined:   'rgba(255,255,255,0.10)',
      buttonOutlined:  'rgba(255,255,255,0.24)',
    },
  },
};

export const colorKeys: {
  palette: PaletteColorKey[];
  common: CommonColorsKeys[];
} = {
  palette: ['primary', 'secondary', 'info', 'success', 'warning', 'error'],
  common: ['black', 'white'],
};
